import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import Pagination from "./Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { formatDateToDDMMYYYY } from "../pages/const";
import { PAYMENT_METHOD_LABELS } from "../utils/paymentLinesErpAdapter";
import {
  getErpRequestErrorMessage,
  isErpApiSuccess,
  postCashbook,
} from "../erpClient";
import { cashbookTxLabel } from "./JamaVoucherPDF";
import { useVisitedPageCache, visitedPageKey } from "../utils/visitedPageCache";

const TX_TYPE_LABELS = {
  CASH_IN: "Cash In",
  CASH_OUT: "Cash Out",
  BANK_IN: "Bank In",
  BANK_OUT: "Bank Out",
};

function formatMoney(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatTableDate(value) {
  if (!value) return "—";
  const iso = String(value).includes("T")
    ? String(value).split("T")[0]
    : String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const formatted = formatDateToDDMMYYYY(iso);
    return formatted || "—";
  }
  return String(value);
}

function formatPartyType(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const key = raw.toLowerCase();
  if (key === "customer") return "Customer";
  if (key === "supplier") return "Supplier";
  if (key === "karigar") return "Karigar";
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

function formatPaymentMode(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const key = raw.toLowerCase().replace(/[\s-]+/g, "_");
  return PAYMENT_METHOD_LABELS[key] || raw;
}

function formatMode(row) {
  const mode = formatPaymentMode(row?.payment_mode);
  const channel = String(row?.channel || "").toUpperCase();
  const tx = String(row?.tx_type || "").toUpperCase();
  const isBank = channel === "BANK" || tx.startsWith("BANK");
  const bankName = String(row?.bank_name || "").trim();
  if (isBank && bankName) {
    return mode ? `${mode} · ${bankName}` : bankName;
  }
  return mode || "—";
}

function isJamaRow(row) {
  const value = row?.is_jama;
  return value === true || value === 1 || value === "1" || value === "true";
}

function formatAgainst(row) {
  if (isJamaRow(row)) return "On account";
  const bills = (Array.isArray(row?.allocations) ? row.allocations : [])
    .map((bill) => String(bill?.bill_no || "").trim())
    .filter(Boolean);
  return bills.length ? bills.join(", ") : "—";
}

function formatTxType(value) {
  const key = String(value || "")
    .trim()
    .toUpperCase();
  return TX_TYPE_LABELS[key] || cashbookTxLabel(value) || "—";
}

export function resolveVoucherShowId(row) {
  if (row?.cash_transaction_id != null && row.cash_transaction_id !== "") {
    return row.cash_transaction_id;
  }
  if (row?.document_id != null && row.document_id !== "") {
    return row.document_id;
  }
  if (row?.doc_number != null && String(row.doc_number).trim() !== "") {
    return row.doc_number;
  }
  return null;
}

function CashbookVoucherTable({
  active = false,
  branchId,
  defaultChannel = "",
  defaultDirection = "",
  refreshToken = 0,
  buttonClass = "",
  onOpenSlip,
}) {
  const { tableClass, cardClass, buttonTableStyle } = useButtonTableStyle();
  const [channel, setChannel] = useState(defaultChannel || "");
  const [direction, setDirection] = useState(defaultDirection || "");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [filtersReady, setFiltersReady] = useState(false);
  const searchRef = useRef("");
  const openingRef = useRef(false);
  const pageCache = useVisitedPageCache();
  const refreshSeenRef = useRef(refreshToken);

  useEffect(() => {
    if (!active) {
      setFiltersReady(false);
      return;
    }
    setChannel(defaultChannel || "");
    setDirection(defaultDirection || "");
    setFromDate("");
    setToDate("");
    setSearchInput("");
    setSearch("");
    searchRef.current = "";
    setPage(1);
    setLimit(20);
    setFiltersReady(true);
  }, [active, defaultChannel, defaultDirection]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchInput.trim();
      if (searchRef.current === next) return;
      searchRef.current = next;
      setSearch(next);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!active || !filtersReady) return undefined;
    if (branchId == null || branchId === "") return undefined;

    if (refreshSeenRef.current !== refreshToken) {
      refreshSeenRef.current = refreshToken;
      pageCache.clear();
    }
    const cacheKey = visitedPageKey([
      branchId,
      page,
      limit,
      channel,
      direction,
      fromDate,
      toDate,
      search,
    ]);
    const cached = pageCache.read(cacheKey);
    if (cached) {
      setItems(cached.items);
      setPagination(cached.pagination);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      const body = {
        type: "list",
        branch_id: Number(branchId) || branchId,
        page,
        limit,
      };
      if (channel) body.channel = channel;
      if (channel && direction) body.tx_type = `${channel}_${direction}`;
      if (fromDate) body.from_date = fromDate;
      if (toDate) body.to_date = toDate;
      if (search) body.search = search;

      try {
        const response = await postCashbook(body);
        if (cancelled) return;
        if (!isErpApiSuccess(response)) {
          toast.error(response?.message || "Unable to load vouchers");
          setItems([]);
          setPagination((prev) => ({ ...prev, total: 0, total_pages: 1 }));
          return;
        }
        const data = response?.data || {};
        const nextItems = Array.isArray(data.items) ? data.items : [];
        const pager = data.pagination || {};
        const nextPage = Number(pager.page) || page;
        const nextLimit = Number(pager.limit) || limit;
        const nextTotal = Number(pager.total);
        const nextPages = Number(pager.total_pages) || 1;
        const nextPagination = {
          page: nextPage,
          limit: nextLimit,
          total: Number.isFinite(nextTotal) ? nextTotal : nextItems.length,
          total_pages: nextPages,
        };
        setItems(nextItems);
        setPagination(nextPagination);
        pageCache.write(cacheKey, { items: nextItems, pagination: nextPagination });
        if (nextPage !== page) setPage(nextPage);
      } catch (error) {
        if (cancelled) return;
        toast.error(
          getErpRequestErrorMessage(error, "Unable to load vouchers"),
        );
        setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    active,
    filtersReady,
    branchId,
    page,
    limit,
    channel,
    direction,
    fromDate,
    toDate,
    search,
    refreshToken,
  ]);

  const openSlip = async (row) => {
    if (openingRef.current) return;
    const id = resolveVoucherShowId(row);
    if (id == null) {
      toast.error("Unable to load voucher");
      return;
    }
    if (branchId == null || branchId === "") {
      toast.error("Branch is required");
      return;
    }
    openingRef.current = true;
    setOpeningId(id);
    try {
      const response = await postCashbook({
        type: "show",
        branch_id: Number(branchId) || branchId,
        id,
      });
      if (!isErpApiSuccess(response)) {
        toast.error(response?.message || "Unable to load voucher");
        return;
      }
      const voucher = response?.data;
      if (!voucher || typeof voucher !== "object" || Array.isArray(voucher)) {
        toast.error("Unable to load voucher");
        return;
      }
      onOpenSlip?.(voucher);
    } catch (error) {
      toast.error(getErpRequestErrorMessage(error, "Unable to load voucher"));
    } finally {
      openingRef.current = false;
      setOpeningId(null);
    }
  };

  if (!active) return null;

  const columnCount = 9;

  return (
    <div>
      <div className="row g-2 align-items-end mb-3">
        <div className="col-6 col-md-2">
          <label className="form-label fw-semibold mb-1">Channel</label>
          <select
            className={`form-select ${buttonClass}`}
            value={channel}
            onChange={(event) => {
              const next = event.target.value;
              setChannel(next);
              if (!next) setDirection("");
              setPage(1);
            }}
          >
            <option value="">All</option>
            <option value="CASH">Cash</option>
            <option value="BANK">Bank</option>
          </select>
        </div>
        <div className="col-6 col-md-2">
          <label className="form-label fw-semibold mb-1">In / Out</label>
          <select
            className={`form-select ${buttonClass}`}
            value={direction}
            disabled={!channel}
            onChange={(event) => {
              setDirection(event.target.value);
              setPage(1);
            }}
          >
            <option value="">All</option>
            <option value="IN">In</option>
            <option value="OUT">Out</option>
          </select>
        </div>
        <div className="col-6 col-md-2">
          <label className="form-label fw-semibold mb-1">From</label>
          <input
            type="date"
            className={`form-control ${buttonClass}`}
            value={fromDate}
            onChange={(event) => {
              setFromDate(event.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="col-6 col-md-2">
          <label className="form-label fw-semibold mb-1">To</label>
          <input
            type="date"
            className={`form-control ${buttonClass}`}
            value={toDate}
            onChange={(event) => {
              setToDate(event.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="col-12 col-md-4">
          <label className="form-label fw-semibold mb-1">Search</label>
          <input
            type="search"
            className={`form-control ${buttonClass}`}
            placeholder="Voucher no., party, or remarks"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </div>
      </div>

      <div
        className={`table-responsive mb-2 border position-relative ${cardClass} ${
          buttonTableStyle === "square" ? "rounded-0" : ""
        }`}
      >
        {openingId != null && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ background: "rgba(255,255,255,0.72)", zIndex: 2 }}
          >
            <div className="spinner-border spinner-border-sm me-2" role="status">
              <span className="visually-hidden">Loading voucher</span>
            </div>
            <span className="text-muted">Loading voucher</span>
          </div>
        )}
        <table
          className={`table text-nowrap mb-0 align-middle ${tableClass} ${
            buttonTableStyle === "square" ? "rounded-0" : ""
          }`}
        >
          <thead className="text-dark fs-4">
            <tr>
              <th>Voucher no.</th>
              <th>Date</th>
              <th>Type</th>
              <th>Party</th>
              <th className="text-end">Amount</th>
              <th>Mode</th>
              <th className="text-wrap" style={{ minWidth: "10rem" }}>
                Against
              </th>
              <th className="text-wrap" style={{ minWidth: "10rem" }}>
                Remarks
              </th>
              <th>Print</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columnCount} className="text-center py-4">
                  Loading...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={columnCount} className="text-center py-5 text-muted">
                  No vouchers found.
                </td>
              </tr>
            ) : (
              items.map((row, index) => {
                const showId = resolveVoucherShowId(row);
                const voucherNo = String(row?.voucher_number || "").trim();
                const isOpening =
                  openingId != null && String(openingId) === String(showId);
                return (
                  <tr
                    key={
                      showId != null
                        ? String(showId)
                        : `${voucherNo || "row"}-${index}`
                    }
                  >
                    <td className="fw-semibold">{voucherNo || "—"}</td>
                    <td>{formatTableDate(row?.tx_date)}</td>
                    <td>{formatTxType(row?.tx_type)}</td>
                    <td>
                      <div className="fw-medium">{row?.party_name || "—"}</div>
                      {formatPartyType(row?.party_type) ? (
                        <div className="text-muted small">
                          {formatPartyType(row?.party_type)}
                        </div>
                      ) : null}
                    </td>
                    <td className="text-end fw-semibold">
                      {formatMoney(row?.amount)}
                    </td>
                    <td>{formatMode(row)}</td>
                    <td className="text-wrap" style={{ minWidth: "10rem" }}>
                      {formatAgainst(row)}
                    </td>
                    <td className="text-wrap" style={{ minWidth: "10rem" }}>
                      {String(row?.remarks || "").trim() || "—"}
                    </td>
                    <td>
                      <button
                        type="button"
                        className={`btn btn-outline-primary btn-sm ${buttonClass}`}
                        disabled={showId == null || openingId != null}
                        onClick={() => openSlip(row)}
                      >
                        {isOpening ? "Loading..." : "Print"}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!loading && pagination.total > 0 && (
        <Pagination
          totalItems={pagination.total}
          itemsPerPage={limit}
          currentPage={page}
          onPageChange={(next) => {
            const last = Math.max(1, Number(pagination.total_pages) || 1);
            setPage(Math.min(Math.max(1, next), last));
          }}
          onItemsPerPageChange={(nextLimit) => {
            setLimit(nextLimit);
            setPage(1);
          }}
        />
      )}
    </div>
  );
}

export default CashbookVoucherTable;
